/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect } from 'react';
import { Formik, Form, FormikErrors, useFormikContext } from 'formik';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import appTheme from '../../common/theme/appTheme.json';
import TitleTextField from './TitleTextField';
import TailSelectField from './TailSelectField';
import AntennaSelectField from './AntennaSelectField';
import HcxButtons from './HcxButtons';
import MarginalServiceSelects from './MarginalServiceSelects';
import SaveDeleteButtons from './SaveDeleteButtons';
import { selectUserId, selectSelectedAntenna, selectSelectedTerminal, selectMissionPlanById } from '../../../store';
import { saveMissionPlan } from '../../../store/missionPlans/actions';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import MissionPlanDetails from '../../../shared/entities/missionPlanDetails';

export interface MissionDetailsFormProps {}
export interface FormValues {
  missionTitle: string;
  tailNumber: string;
  antennaType: string;
  hcx: boolean;
  takeoffBuffer: string;
  handoffBuffer: string;
  landingBuffer: string;
}

export enum FormikFieldEnum {
  MISSION_TITLE = 'missionTitle',
  TAIL_NUMBER = 'tailNumber',
  ANTENNA_TYPE = 'antennaType',
  HCX = 'hcx',
  TAKEOFF_BUFFER = 'takeoffBuffer',
  HANDOFF_BUFFER = 'handoffBuffer',
  LANDING_BUFFER = 'landingBuffer',
}

export type FormikValueType = string | number | boolean;

export const FormikFieldDefaultValues = {
  missionTitle: '',
  tailNumber: '',
  antennaType: '',
  hcx: false,
  takeoffBuffer: '15',
  handoffBuffer: '10',
  landingBuffer: '15',
};

const useStyles = makeStyles({
  label: {
    color: appTheme.mptFormLabelText,
    fontWeight: 600,
    fontSize: '14px',
    fontFamily: appTheme.appFontFamily,
    lineHeight: '35px',
  },
  input: {
    color: appTheme.primaryTextColor,
    fontSize: '16px',
    fontFamily: appTheme.appFontFamily,
    border: `1px solid ${appTheme.primaryTextColor}`,
    borderRadius: '5px',
    backgroundColor: appTheme.mptPrimaryBackgroundColor,
    paddingLeft: '10px',
    WebkitBoxSizing: 'border-box',
    MozBoxSizing: 'border-box',
    boxSizing: 'border-box',
    width: '100%',
    height: '38px',
  },
  error: { color: appTheme.mptPrimaryRed, fontSize: '14px', marginTop: '5px', width: '100%' },
});

// Reusable hook to populate formik value
export function useAutopopulateFormikValue(formikField: FormikFieldEnum, value: FormikValueType) {
  const { setFieldValue } = useFormikContext<FormValues>();
  const missionPlanToEdit = useSelector(selectMissionPlanById, shallowEqual);
  useEffect(() => {
    if (missionPlanToEdit.id !== null) {
      setFieldValue(formikField, value, false);
    } else {
      setFieldValue(formikField, FormikFieldDefaultValues[formikField], false);
    }
  }, [missionPlanToEdit, setFieldValue, formikField, value]);
}

const MissionDetailsForm = (props: MissionDetailsFormProps) => {
  const classes = useStyles();
  const userId = useSelector(selectUserId, shallowEqual);
  const antenna = useSelector(selectSelectedAntenna, shallowEqual);
  const terminal = useSelector(selectSelectedTerminal, shallowEqual);
  const missionPlanToEdit = useSelector(selectMissionPlanById, shallowEqual);
  const dispatch = useDispatch();

  const validate = (values: FormValues) => {
    const errors: FormikErrors<FormValues> = {};

    if (!values.missionTitle) {
      errors.missionTitle = 'Required';
    } else if (!/^[a-zA-Z0-9_/\s-]{1,40}$/.test(values.missionTitle)) {
      errors.missionTitle = 'Only 40 letters, numbers, spaces, underscores, forward slashes, and hyphens';
    }

    if (values.tailNumber === '') {
      errors.tailNumber = 'Required';
    }

    if (values.antennaType === '') {
      errors.antennaType = 'Required';
    }

    return errors;
  };

  const submitForm = (values: FormValues) => {
    if (antenna === undefined || terminal === undefined) return;

    const plan: MissionPlanDetails = {
      aircraftFti: null,
      aircraftTailNum: values.tailNumber,
      aircraftType: null,
      aircraftTypeId: 0,
      antennaType: antenna.name,
      antennaTypeId: antenna.id,
      creationDate: missionPlanToEdit.creationDate,
      handOffBuffer: values.handoffBuffer ? parseInt(values.handoffBuffer) : null,
      id: missionPlanToEdit.id,
      landingBuffer: values.landingBuffer ? parseInt(values.landingBuffer) : null,
      name: values.missionTitle,
      sedFileVersion: terminal.sedFileVer,
      sscfFileVersion: terminal.sscfFileVer,
      viHnaPolicyVersion: terminal.viHnaPolicyVersion || null,
      takeOffBuffer: values.takeoffBuffer ? parseInt(values.takeoffBuffer) : null,
      datapathUuid: terminal.datapathUuid,
      uniqueId: '0000000000000-0',
      useHcxBeams: values.hcx,
      useNmsConfiguredMapVersion: true,
      user: userId,
    };

    //Post request to /api/mpt/missionplans
    dispatch(saveMissionPlan(plan));
  };

  return (
    <React.Fragment>
      <Formik
        initialValues={{
          missionTitle: FormikFieldDefaultValues[FormikFieldEnum.MISSION_TITLE],
          tailNumber: FormikFieldDefaultValues[FormikFieldEnum.TAIL_NUMBER],
          antennaType: FormikFieldDefaultValues[FormikFieldEnum.ANTENNA_TYPE],
          hcx: FormikFieldDefaultValues[FormikFieldEnum.HCX],
          takeoffBuffer: FormikFieldDefaultValues[FormikFieldEnum.TAKEOFF_BUFFER],
          handoffBuffer: FormikFieldDefaultValues[FormikFieldEnum.HANDOFF_BUFFER],
          landingBuffer: FormikFieldDefaultValues[FormikFieldEnum.LANDING_BUFFER],
        }}
        initialTouched={{
          missionTitle: true,
        }}
        validate={validate}
        validateOnBlur={false}
        validateOnChange={true}
        onSubmit={values => {
          submitForm(values);
        }}
      >
        <Form translate="yes">
          <Box display="flex" flexWrap="wrap" rowGap="20px" justifyContent="space-between">
            <TitleTextField styling={classes} />
            <TailSelectField styling={classes} />
            <AntennaSelectField styling={classes} />
            <HcxButtons styling={classes} />
            <MarginalServiceSelects styling={classes} />
            <SaveDeleteButtons />
          </Box>
        </Form>
      </Formik>
    </React.Fragment>
  );
};

export default MissionDetailsForm;
